/**
 * Builds the single global CommandContext the keyboard dispatcher and the
 * command palette run against.
 *
 * Ownership is deliberate. The list pane handles j/k/e/# itself and never
 * registers a `list` scope, and the composer registers its own `composer`
 * scope while it is open. This shell therefore registers `global` only, and
 * SHELL_COMMANDS strips the commands nothing here can honour so the palette
 * never offers an action that would quietly do nothing.
 */
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, type PublicAccount, type ThreadDetail } from '../lib/api';
import {
  commands as allCommands,
  type Command,
  type CommandContext,
  type GoTarget,
} from '../lib/keymap';
import type { ComposerIntent } from '../components/Composer';

/**
 * Commands with no implementation behind them in this build:
 *  - spam / mute / label / move have no endpoint in the API contract;
 *  - undo belongs to the list pane's own trash-undo toast;
 *  - send.flush belongs to the composer's pending-send queue;
 *  - drafts is not one of the server's thread views.
 */
const UNSUPPORTED = new Set([
  'triage.spam',
  'triage.mute',
  'triage.label',
  'triage.move',
  'edit.undo',
  'send.flush',
  'go.drafts',
]);

/** What the keyboard dispatcher is allowed to fire. */
export const SHELL_COMMANDS: Command[] = allCommands.filter((c) => !UNSUPPORTED.has(c.id));

/**
 * What the palette lists. Navigation needs a cursor the palette cannot move,
 * and compose actions only mean anything inside an open composer.
 */
export const PALETTE_COMMANDS: Command[] = SHELL_COMMANDS.filter(
  (c) => c.group !== 'Navigation' && c.scope !== 'composer',
);

/** Palette snoozes land here: tomorrow morning, local time. */
function tomorrowMorning(): number {
  const when = new Date();
  when.setDate(when.getDate() + 1);
  when.setHours(9, 0, 0, 0);
  return Math.floor(when.getTime() / 1000);
}

export interface AppCommandDeps {
  accounts: PublicAccount[];
  accountFilter: number | null;
  aiEnabled: boolean;
  selectedThreadKey: string | null;
  setAccountFilter: (id: number | null) => void;
  goTo: (target: GoTarget) => void;
  compose: (intent: ComposerIntent) => void;
  openPalette: (mode: 'palette' | 'cheatsheet') => void;
  focusSearch: (allAccounts?: boolean) => void;
  goBack: () => void;
  toggleSidebar: () => void;
  toggleFocusMode: () => void;
  cycleSplit: (direction: 1 | -1) => void;
}

export function useAppCommandContext(deps: AppCommandDeps): CommandContext {
  const queryClient = useQueryClient();

  const {
    accounts,
    accountFilter,
    aiEnabled,
    selectedThreadKey,
    setAccountFilter,
    goTo,
    compose,
    openPalette,
    focusSearch,
    goBack,
    toggleSidebar,
    toggleFocusMode,
    cycleSplit,
  } = deps;

  return useMemo<CommandContext>(() => {
    /**
     * Thread mutations are slow (they reach IMAP). Fire and let the realtime
     * event or this settle invalidate; the list pane owns the optimistic paint
     * for actions started from the list itself.
     */
    const act = (
      action: Parameters<typeof api.threadAction>[1],
      body?: Parameters<typeof api.threadAction>[2],
    ) => {
      const key = selectedThreadKey;
      if (!key) return;
      void api
        .threadAction(key, action, body)
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: ['threads'] });
          void queryClient.invalidateQueries({ queryKey: ['thread', key] });
        });
    };

    /**
     * Reachable by neither the dispatcher nor the palette: the list pane owns
     * the cursor and selection keys directly, and the composer overrides the
     * compose actions in its own scope while it is open. CommandContext is a
     * total interface, so the members still have to exist.
     */
    const ownedElsewhere = () => {};

    /** The cache already holds the open thread, so a toggle can be a real toggle. */
    const openThread = selectedThreadKey
      ? queryClient.getQueryData<ThreadDetail>(['thread', selectedThreadKey])
      : undefined;

    return {
      accounts,
      accountFilter,
      aiEnabled,
      selection: { threadKey: selectedThreadKey, count: selectedThreadKey ? 1 : 0 },
      hasPendingSend: false,

      moveCursor: ownedElsewhere,
      moveCursorToEdge: ownedElsewhere,
      openSelected: ownedElsewhere,
      goBack,
      moveMessageCursor: ownedElsewhere,
      scrollReading: ownedElsewhere,
      cycleSplit,

      goTo,
      setAccountFilter,
      toggleSidebar,
      toggleFocusMode,

      archive: (restore) => act('archive', { restore: restore === true }),
      trash: () => act('trash'),
      spam: ownedElsewhere,
      snooze: () => act('snooze', { until: tomorrowMorning() }),
      toggleStar: () => act('star', { value: !openThread?.thread.flagged }),
      toggleRead: () => act('read', { value: (openThread?.thread.unreadCount ?? 1) > 0 }),
      mute: ownedElsewhere,
      toggleSelect: ownedElsewhere,
      extendSelection: ownedElsewhere,
      selectAll: ownedElsewhere,
      label: ownedElsewhere,
      move: ownedElsewhere,
      undo: ownedElsewhere,

      compose: (popout) => compose({ mode: 'new', popout: popout === true }),
      reply: (all, popout) =>
        compose({
          mode: all ? 'reply-all' : 'reply',
          threadKey: selectedThreadKey,
          popout: popout === true,
        }),
      forward: (popout) =>
        compose({ mode: 'forward', threadKey: selectedThreadKey, popout: popout === true }),
      send: ownedElsewhere,
      sendAndArchive: ownedElsewhere,
      sendLater: ownedElsewhere,
      discardDraft: ownedElsewhere,
      flushPendingSend: ownedElsewhere,
      openAiInstruction: ownedElsewhere,

      openPalette: () => openPalette('palette'),
      openCheatSheet: () => openPalette('cheatsheet'),
      focusSearch,
    };
  }, [
    accounts,
    accountFilter,
    aiEnabled,
    selectedThreadKey,
    queryClient,
    setAccountFilter,
    goTo,
    compose,
    openPalette,
    focusSearch,
    goBack,
    toggleSidebar,
    toggleFocusMode,
    cycleSplit,
  ]);
}
